import React, { Fragment, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  IconButton,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ModeEditOutlineRoundedIcon from "@mui/icons-material/ModeEditOutlineRounded";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const TextFieldDatePicker = styled(TextField)({
  "& .MuiFilledInput-root": {
    backgroundColor: "#919eab14",
    height: 56,
    padding: "0px 12px",
    borderRadius: 8,
    "&.Mui-focused": {
      backgroundColor: "#919eab14",
    },
    "& .MuiInputBase-input": {
      padding: "4px 0 5px 8px",
    },
    "&:hover": {
      backgroundColor: "#919eab29",
      "&:before": {
        border: "none !important",
      },
    },
    "&::after": {
      border: "none",
    },
    "&::before": {
      border: "none",
      borderBottomStyle: "none !important",
    },
  },
});

const StyledCellHeader = styled(TableCell)({
  padding: "13.5px 16px",
  borderBottom: "none",
  color: "#637381",
  backgroundColor: "#f4f6f8",
  "&.sticky": {
    position: "sticky",
    left: 0,
    backgroundColor: "#f4f6f8",
    boxShadow: "20px -10px 20px #EEEEEE",
    zIndex: 4,
  },
  "&:first-of-type": {
    paddingLeft: 24,
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    backgroundColor: "#f4f6f8",
  },
  "&:last-of-type": {
    paddingRight: 24,
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  },
});

const StyledEmpty = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: 350,
});

const StyledCellContent = styled(TableCell)({
  borderBottom: "1px dashed rgba(224, 224, 224, 1)",
  padding: "8px 16px",
});

const IconButtonStyled = styled(IconButton)({
  color: "#007afe",
});

const HolidayPatternList = ({
  handleOpenDrawerHoliday,
  isReadOnly
}) => {
  const { t, i18n } = useTranslation();
  const { result: holidayStore } = useSelector((state) => state.holiday);
  const [selectYear, setSelectYear] = useState(dayjs());
  const [listHoliday, setListHoliday] = useState([]);

  useEffect(() => {
    onChangeYear(selectYear);
  }, [holidayStore]);

  const handleClickChangeYear = (newValue) => {
    setSelectYear(newValue);
    onChangeYear(newValue);
  };

  const onChangeYear = (year) => {
    if (
      holidayStore &&
      year &&
      holidayStore[year.year()] &&
      holidayStore[year.year()].length > 0
    ) {
      setListHoliday(holidayStore[year.year()]);
    } else {
      setListHoliday([]);
    }
  };

  return (
    <Fragment>
      <Grid
        container
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
        style={{ marginTop: "24px" }}
      >
        <Grid item>
          <Typography variant="h6">{t("ListOfAnnualHolidays")}</Typography>
        </Grid>
        <Grid item>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={i18n.language}
            dateFormats={{
              year: i18n.language === "th" ? "BBBB" : "YYYY"
            }}
          >
            <DatePicker
              views={["year"]}
              minDate={dayjs()}
              maxDate={dayjs().add(1, "year")}
              value={selectYear}
              onChange={(newValue) => handleClickChangeYear(newValue)}
              inputFormat={i18n.language === 'th' ? "BBBB": "YYYY"}
              renderInput={(params) => (
                <TextFieldDatePicker
                  {...params}
                  variant="filled"
                  style={{
                    width: "150px",
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
      <Stack
        style={{
          marginTop: "16px",
        }}
      >
        {listHoliday && listHoliday.length > 0 ? (
          <Fragment>
            {listHoliday.map((holidayPattern) => (
              <Accordion key={holidayPattern.idHolidayPattern}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  {holidayPattern.holidayPatternName}
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer style={{ width: "100%", minHeight: "450px" }}>
                    <Table aria-label="table">
                      <TableHead>
                        <TableRow>
                          <StyledCellHeader align="center" 
                            style={{
                              minWidth:"375px"
                            }}
                          >
                            {t("Date")}/{t("HolidayName")}
                          </StyledCellHeader>
                          <StyledCellHeader align="center"
                            style={{
                              minWidth:"375px"
                            }}
                          >
                            {t("Date")}/{t("NameOfHolidayCompensate")}
                          </StyledCellHeader>
                          <StyledCellHeader align="center" 
                            style={{
                              width: "218px"
                            }}
                          >
                            {t("Status")}
                          </StyledCellHeader>
                          {isReadOnly ? (<Fragment></Fragment>) : (
                            <StyledCellHeader align="center"
                              style={{
                                width: "218px"
                              }}
                            >
                              {t("Manage")}
                            </StyledCellHeader>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {holidayPattern.listHoliday.map((holiday, index) => (
                          <TableRow key={index}>
                            <StyledCellContent>
                              <Typography variant="subtitle1">
                                {dayjs(holiday.dateHoliday).format(
                                  `DD MMM ${
                                    i18n.language === "th"
                                      ? "BBBB"
                                      : "YYYY"
                                  }`
                                )}
                              </Typography>
                              <Typography color="text.third">
                                {i18n.language === "th"
                                  ? holiday.name
                                  : holiday.name_EN
                                  ? holiday.name_EN
                                  : holiday.name || "-"}
                              </Typography>
                            </StyledCellContent>
                            <StyledCellContent>
                              <Typography variant="subtitle1">
                                {holiday.compensateDate
                                  ? dayjs(holiday.compensateDate).format(
                                      `DD MMM ${
                                        i18n.language === "th"
                                          ? "BBBB"
                                          : "YYYY"
                                      }`
                                    )
                                  : "-"}
                              </Typography>
                              <Typography color="text.third">
                                {i18n.language === "th"
                                  ? holiday.compensateName
                                    ? holiday.compensateName
                                    : holiday.compensateName_EN || "-"
                                  : holiday.compensateName_EN
                                  ? holiday.compensateName_EN
                                  : holiday.compensateName || "-"}
                              </Typography>
                            </StyledCellContent>
                            <StyledCellContent align="center">
                              {holiday.isActive === 1
                                ? `${t("Use")}`
                                : `${t("NotInUse")}`}
                            </StyledCellContent>
                            {isReadOnly ? (<Fragment></Fragment>) : (
                              <StyledCellContent align="center">
                                <IconButtonStyled
                                  onClick={() => {
                                    handleOpenDrawerHoliday({
                                      type: "Edit",
                                      holiday: holiday
                                    });
                                  }}
                                >
                                  <ModeEditOutlineRoundedIcon
                                    style={{ fontSize: "20px" }}
                                  />
                                </IconButtonStyled>
                              </StyledCellContent>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
          </Fragment>
        ) : (
          <StyledEmpty className="empty-holiday">
            <Typography color="text.secondary" align="center">
              {t("NoHolidays")}
            </Typography>
          </StyledEmpty>
        )}
      </Stack>
    </Fragment>
  );
};

export default HolidayPatternList;
